import React from 'react';
import { Card } from 'antd';
import { useLocation } from 'react-router-dom';
import { SkBar } from './_primitives';

const TAB_ORDER = ['smtp', 'sms', 'marn'];

const ConfigurationHeader = () => (
  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 mb-4 sm:mb-6">
    <div className="flex flex-col gap-2 min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <SkBar h="h-3" w="w-14" />
        <SkBar h="h-3" w="w-2" />
        <SkBar h="h-3" w="w-28" />
      </div>
      <SkBar h="h-7" w="w-56" className="max-w-full mt-1" />
      <SkBar h="h-3" w="w-80" className="max-w-full" />
    </div>
    <SkBar h="h-9" w="w-36" className="rounded-md flex-shrink-0" />
  </div>
);

const IconTab = ({ active = false, labelW = 'w-16' }) => (
  <div
    className={`pb-2 flex items-center gap-2 flex-shrink-0 ${
      active ? 'border-b-2 border-gray-300' : ''
    }`}
  >
    <div className="w-4 h-4 rounded bg-gray-200 animate-pulse" />
    <SkBar h="h-4" w={labelW} />
  </div>
);

const InnerCardTitle = () => (
  <>
    <div className="flex items-center gap-3 mb-2">
      <div className="w-9 h-9 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
      <SkBar h="h-5" w="w-56" />
    </div>
    <SkBar h="h-3" w="w-96" className="max-w-full mb-6" />
  </>
);

const SaveButtonRow = ({ labelW = 'w-44' }) => (
  <div className="flex justify-end mt-6">
    <SkBar h="h-10" w={labelW} className="rounded-md" />
  </div>
);

/* ─────────── SMTP tab ─────────── */

const FormField = () => (
  <div className="flex flex-col gap-2">
    <SkBar h="h-3" w="w-24" />
    <SkBar h="h-10" w="w-full" className="rounded-md" />
  </div>
);

const ToggleRow = ({ on = true }) => (
  <div className="flex items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg mb-3">
    <div className="flex-1 min-w-0 flex flex-col gap-2">
      <SkBar h="h-4" w="w-24" />
      <SkBar h="h-3" w="w-72" className="max-w-full" />
    </div>
    <SkBar
      h="h-6"
      w="w-12"
      className={`rounded-full flex-shrink-0 ${on ? 'bg-gray-300' : ''}`}
    />
  </div>
);

const SmtpTabContent = () => (
  <Card className="shadow-none border border-gray-200" styles={{ body: { padding: 20 } }}>
    <InnerCardTitle />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <FormField />
      <FormField />
      <FormField />
      <FormField />
    </div>
    <ToggleRow on />
    <ToggleRow on={false} />
    <SaveButtonRow />
  </Card>
);

/* ─────────── SMS tab ─────────── */

const FieldWithHint = ({ fullWidth = true }) => (
  <div className="flex flex-col gap-2">
    <SkBar h="h-3" w="w-28" />
    <SkBar h="h-10" w={fullWidth ? 'w-full' : 'w-full'} className="rounded-md" />
    <SkBar h="h-2" w="w-48" className="max-w-full" />
  </div>
);

const CurrentConfigSection = () => (
  <div className="mt-6 pt-6 border-t border-gray-200">
    <SkBar h="h-5" w="w-48" className="mb-4" />
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="p-3 border border-gray-200 rounded-lg flex flex-col gap-2"
        >
          <SkBar h="h-3" w="w-20" />
          <SkBar h="h-4" w="w-full" />
        </div>
      ))}
    </div>
  </div>
);

const SmsTabContent = () => (
  <Card className="shadow-none border border-gray-200" styles={{ body: { padding: 20 } }}>
    <InnerCardTitle />

    {/* SMS API URL (full width) */}
    <div className="mb-4">
      <FormField />
    </div>

    {/* SMS Token with hint */}
    <div className="mb-4">
      <FieldWithHint />
    </div>

    {/* Transactional + Promotional Sender ID */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <FieldWithHint />
      <FieldWithHint />
    </div>

    <SaveButtonRow labelW="w-40" />

    <CurrentConfigSection />
  </Card>
);

/* ─────────── Marn tab ─────────── */

const MarnTabContent = () => (
  <Card className="shadow-none border border-gray-200" styles={{ body: { padding: 20 } }}>
    <InnerCardTitle />

    {/* Single-column stacked URL fields */}
    <div className="flex flex-col gap-4 mb-4">
      {Array.from({ length: 5 }).map((_, i) => (
        <FormField key={i} />
      ))}
    </div>

    {/* API Token (password-style field with eye icon) */}
    <div className="flex flex-col gap-2 mb-4">
      <SkBar h="h-3" w="w-20" />
      <div className="relative">
        <SkBar h="h-10" w="w-full" className="rounded-md" />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded bg-gray-200 animate-pulse" />
      </div>
    </div>

    {/* Brand Key */}
    <div className="mb-4">
      <FormField />
    </div>

    <SaveButtonRow labelW="w-44" />

    <CurrentConfigSection />
  </Card>
);

/* ─────────── Main loader ─────────── */

const ConfigurationPageLoader = () => {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab') || 'smtp';
  const activeIndex = Math.max(0, TAB_ORDER.indexOf(tab));

  let content;
  if (tab === 'sms') content = <SmsTabContent />;
  else if (tab === 'marn') content = <MarnTabContent />;
  else content = <SmtpTabContent />;

  return (
    <div className="w-full p-3 sm:p-4 md:p-6">
      <ConfigurationHeader />

      <Card className="shadow-sm" styles={{ body: { padding: 20 } }}>
        {/* Tabs */}
        <div className="flex gap-8 border-b border-gray-200 mb-6 overflow-x-auto">
          <IconTab active={activeIndex === 0} labelW="w-16" />
          <IconTab active={activeIndex === 1} labelW="w-14" />
          <IconTab active={activeIndex === 2} labelW="w-14" />
        </div>

        {content}
      </Card>
    </div>
  );
};

export default ConfigurationPageLoader;
